import { useEffect, useState, forwardRef } from "react"

import { useTranslation } from "lib/i18n/translation"
import { useModal } from "lib/modal"

import { Loading } from "components/films"
import MovementModal from "components/modals/movement"

import ProductsFilters from "./products-filters-modal"
import ProductsFiltersFooter from "./products-filters-modal-footer"

import {
	useProducts,
	useProductsFilters,
	CATEGORY_FILTER,
	DISPLAY_FILTER,
	TOP_FILTER,
	BOTTOM_FILTER,
	CUSTOM_FILTER,
	MOVEMENT_FILTER,
	YEAR_CATEGORY,
	ALL_FILMS_CATEGORY
} from "lib/products"
import { ACCESSIBILITY } from "lib/tools/categories-list"
import { useCountry } from "lib/country"

const alphanumericals = ["#", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"]

export const checkFilterDisplay = (filter, country) => {
	return (filter.title === ACCESSIBILITY && country === "fr") || filter.title !== ACCESSIBILITY
}

const Navigation = forwardRef(({ baseFilters, setSectionsHeight, currentAlpha, goToAlpha }, ref) => {

	const { c } = useTranslation("common")

	const { catalog } = useProducts()
	const {
		filters,
		updateFilters,
		availableFilters,
		loading
	} = useProductsFilters()
	const { show } = useModal()
	const { country } = useCountry()

	const baseFilterStatus = {}
	availableFilters.bottom.forEach(filter => {
		baseFilterStatus[filter.title] = false
	})
	const [filterStatus, setFilterStatus] = useState(baseFilterStatus)
	const [filtersCollapsed, setFiltersCollapsed] = useState(false)

	const showMovement = (movement) => {
		show({
			id: `movement-${movement.id}-modal`,
			children: <MovementModal movement={movement} />,
			className: "slide left"
		})
	}

	const toggleFilters = () => {
		setFiltersCollapsed(!filtersCollapsed)
	}

	const toggleFilter = (type) => {
		let newFilterStatus = Object.assign({}, filterStatus)
		if (filterStatus[type]) {
			newFilterStatus[type] = false
			setFilterStatus(() => newFilterStatus)
		} else {
			if (Object.values(newFilterStatus).filter(f => f).length == 0) {
				newFilterStatus[type] = true
				setFilterStatus(() => newFilterStatus)
			} else {
				Object.keys(newFilterStatus).forEach(key => {
					newFilterStatus[key] = false
				})
				setFilterStatus(() => newFilterStatus)
				setTimeout(() => {
					newFilterStatus = Object.assign({}, newFilterStatus)
					newFilterStatus[type] = true
					setFilterStatus(() => newFilterStatus)
				}, 400)
			}
		}
	}

	const addBottomFilter = (e, filter) => {
		e.preventDefault();
		updateFilters(BOTTOM_FILTER, filters.bottom.concat([filter]))
	}

	const removeBottomFilter = (e, filter) => {
		e.preventDefault();
		updateFilters(BOTTOM_FILTER, filters[BOTTOM_FILTER].filter(f => f.id !== filter.id))
	}

	const removeCustomFilter = (e, filter) => {
		e.preventDefault();
		updateFilters(CUSTOM_FILTER, filters[CUSTOM_FILTER].filter(f => f.name !== filter.name))
	}

	const updateCategoryFilter = (title) => {
		if (filters[CATEGORY_FILTER] == YEAR_CATEGORY && title !== YEAR_CATEGORY) {
			setSectionsHeight(alphanumericals.map(a => ({ key: a, offset: 0, top: 0 })))
		} else if (filters[CATEGORY_FILTER] == YEAR_CATEGORY && title == YEAR_CATEGORY) {
			setSectionsHeight([])
		}
		updateFilters(CATEGORY_FILTER, title)
	}

	const [fixed, setFixed] = useState(false)

	useEffect(() => {
		setSectionsHeight(alphanumericals.map(a => ({ key: a, offset: 0, top: 0 })))

		if (baseFilters) {
			baseFilters.forEach(f => {
				if (f.key == BOTTOM_FILTER) {
					updateFilters(f.key, filters.bottom.concat([f.value]))
				} else if (f.key == CUSTOM_FILTER) {
					updateFilters(f.key, filters.custom.concat([f.value]))
				}
			})
		}
		window.addEventListener('scroll', handleScroll)

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	const handleScroll = () => {
		if (window.scrollY > 120) {
			setFixed(true)
		}
		if (window.scrollY <= 120) {
			setFixed(false)
		}
	}

	const showFilters = () => {
		show({
			id: "products-filter-modal",
			contentClassName: "has-sticky-aside",
			children: <ProductsFilters />,
			footer: <ProductsFiltersFooter />,
			footerClassName: "fixed"
		})
	}

	return (
		<nav className={`contextual ${fixed ? "fixed" : ""}`} ref={ref} aria-label={c("films-contextual-navigation")}>
			<nav className="filters red large main-filters" aria-label={c("films-filters")}>
				<span>{c("presented-by")}</span>
				{availableFilters.categories.map((filter, index) => (
					<a
						href="#"
						rel="nofollow noopener"
						key={`presented-by-${index}`}
						title={c(filter.title)}
						data-display="title"
						className={`icon-${filter.icon} ${filters[CATEGORY_FILTER] == filter.title ? "active" : ""}`}
						aria-current={filters[CATEGORY_FILTER] == filter.title ? "true" : ""}
						onClick={e => { e.preventDefault(); updateCategoryFilter(filter.title) }}
					>
						{c(filter.title)}
					</a>
				))}
			</nav>

			<nav data-grid="#films-catalog .grid" className="mode switch" aria-label={c("display-mode")}>
				<span>{c("display-by")}</span>
				{availableFilters.display.map((display, index) => (
					<a
						href="#"
						rel="nofollow noopener"
						key={`display-by-${index}`}
						title={c(`display-${display.title}`)}
						data-mode={display.title}
						className={filters[DISPLAY_FILTER] == display.title ? "active" : ""}
						onClick={e => { e.preventDefault(); updateFilters(DISPLAY_FILTER, display.title) }}
					>
						{c(`display-${display.title}`)}
					</a>
				))}
			</nav>

			<nav
				className={`filters ${filters[CATEGORY_FILTER] == YEAR_CATEGORY ? "chronological" : (filters[CATEGORY_FILTER] == ALL_FILMS_CATEGORY ? "alphanumerical" : "alphabetical")} grey`}
				aria-label={c(`${filters[CATEGORY_FILTER] == YEAR_CATEGORY ? "chronological" : (filters[CATEGORY_FILTER] == ALL_FILMS_CATEGORY ? "alphanumerical" : "alphabetical")}-filters`)}
			>
				{filters[CATEGORY_FILTER] == YEAR_CATEGORY
					? catalog.filter(s => s.title).map(({ title }) => (
						<a
							href="#"
							onClick={e => { e.preventDefault(); goToAlpha(title) }}
							rel="nofollow noopener"
							title="#"
							className={`${!catalog || !catalog.find(s => s.title == title) ? "disabled" : ""} ${currentAlpha == title ? "active" : ""}`}
							aria-current={currentAlpha == title ? "true" : ""}
						>
							{title}
						</a>
					))
					: filters[CATEGORY_FILTER] == ALL_FILMS_CATEGORY
						? alphanumericals.map(alpha => (
							<a
								href="#"
								onClick={e => { e.preventDefault(); goToAlpha(alpha) }}
								rel="nofollow noopener"
								title="#"
								className={`${!catalog || !catalog.find(s => s.title == alpha) ? "disabled" : ""} ${currentAlpha == alpha ? "active" : ""}`}
								aria-current={currentAlpha == alpha ? "true" : ""}
							>
								{alpha}
							</a>
						))
						: alphanumericals.map(alpha => (
							alpha !== "#" &&
							<a
								href="#"
								onClick={e => { e.preventDefault(); goToAlpha(alpha) }}
								rel="nofollow noopener"
								title="#"
								className={`${!catalog || !catalog.find(s => s.title == alpha) ? "disabled" : ""} ${currentAlpha == alpha ? "active" : ""}`}
								aria-current={currentAlpha == alpha ? "true" : ""}
							>
								{alpha}
							</a>
						))
				}
			</nav>

			<nav className="filters-wrapper" aria-label={c("films-filters")}>
				<a
					href="#"
					onClick={e => { e.preventDefault(); toggleFilters() }}
					rel="nofollow noopener"
					title={`${filtersCollapsed ? c("hide") : c("show")} ${c("filters")}`}
					className={`collapsible-toggle filters-expand icon-filters ${filtersCollapsed ? "active" : ""}`}
				>
					{`${filtersCollapsed ? c("hide") : c("show")} ${c("filters")}`}
				</a>
				<nav className="filters-tags current" aria-label={c("selected-keywords")}>
					<a
						href="#"
						onClick={e => { e.preventDefault; showFilters() }}
						rel="nofollow noopener"
						className="filters-tags-current"
					>
						{c("your-filters")} : {(filters[BOTTOM_FILTER].length == 0 && filters[CUSTOM_FILTER].length == 0) ? c("none") : ""}
					</a>
					{filters[BOTTOM_FILTER].map((filter, index) => (
						<a
							href="#"
							key={`bottom-filter-${index}`}
							rel="nofollow noopener"
							className="active"
							onClick={e => { e.preventDefault(); removeBottomFilter(e, filter) }}
							title={filter.name}
						>
							{filter.name}
						</a>
					))
					}

					{filters[CUSTOM_FILTER].map((filter, index) => (
						<a
							href="#"
							key={`custom-filter-${index}`}
							rel="nofollow noopener"
							className="active"
							onClick={e => { e.preventDefault(); removeCustomFilter(e, filter) }}
							title={filter.name}
						>
							{filter.name}
						</a>
					))
					}
				</nav>
			</nav>

			{availableFilters.top && (
				<nav className="filters checkboxes only-desktop" aria-label={c("films-availability-desktop")}>
					{availableFilters.top.map((filter, index) => (
						<a
							href="#"
							rel="nofollow noopener"
							key={`filters-${index}`} title={c(`top-filter-${filter.title}`)}
							className={filters.top == filter.title ? "active" : ""}
							onClick={e => { e.preventDefault(); updateFilters(TOP_FILTER, filter.title) }}
						>
							{c(`top-filter-${filter.title}`)}
						</a>
					))}
				</nav>
			)}

			<nav id="filters-films" className={`collapsible ${(filtersCollapsed || !fixed) ? "active" : ""}`}>
				{!loading && (
					<>
						{availableFilters.top && (
							<nav className="filters checkboxes only-mobile" aria-label={c("films-availability-mobile")}>
								{availableFilters.top.map((filter, index) => (
									<a
										key={`filters-${index}`}
										title={c(`top-filter-${filter.title}`)}
										className={filters.top == filter.title ? "active" : ""}
										onClick={e => { e.preventDefault(); updateFilters(TOP_FILTER, filter.title) }}
									>
										{c(`top-filter-${filter.title}`)}
									</a>
								))}
							</nav>
						)}

						<nav className="filters for-filters-tags" aria-label="filters-choice">
							<span>{c("filter-by")}</span>
							{availableFilters.bottom.map((filter, index) => checkFilterDisplay(filter, country.default_language) && (
								<a
									href="#"
									rel="nofollow noopener"
									key={`filter-by-${index}`}
									title="Filtres"
									className={`collapsible-toggle ${filterStatus[filter.title] ? "active" : ""}`}
									onClick={e => { e.preventDefault(); toggleFilter(filter.title) }}
								>
									{c(`filter-type-${filter.title}`)}
								</a>
							))}
							<a
								href="#"
								onClick={e => { e.preventDefault(); showFilters() }}
								rel="nofollow noopener"
								title={c("all-filters")}
								data-overlay="#overlay-filters-films"
								className="icon-filters"
							>
								{c("all-filters")}
							</a>
						</nav>

						{availableFilters.bottom.map((filter, index) => checkFilterDisplay(filter, country.default_language) && (
							<div
								key={`filters-tags-${index}`}
								id={`filters-films-${filter.title}`}
								className={`collapsible light has-filters-tags ${filterStatus[filter.title] ? "active" : ""}`}
							>
								<nav className="filters-tags" aria-label={c(`filters-by-${filter.title}`)}>
									{filter.values.map((fValue, subindex) => {
										const active = filters[BOTTOM_FILTER].map(f => f.id).includes(fValue.id)

										return <>
											{filter.title == MOVEMENT_FILTER
												? <nav key={`filters-tags-${index}-${subindex}`} aria-label={c("filter-or-learn-more-about")}>
													<a
														href="#"
														rel="nofollow noopener"
														title={fValue.name}
														onClick={e => { e.preventDefault(); active ? removeBottomFilter(e, fValue) : addBottomFilter(e, fValue) }}
														className={active ? "active" : ""}
													>
														{fValue.name}
													</a>
													<a
														href="#"
														rel="nofollow noopener"
														data-overlay="#overlay-movement"
														title={c("learn-more-about")}
														className="about-toggle icon-about"
														onClick={e => { e.preventDefault(); showMovement(fValue) }}
													>
														<span className="label">{c("learn-more-about")}</span>
													</a>
												</nav>
												: <a
													href="#"
													rel="nofollow noopener"
													title={fValue.name}
													onClick={e => { e.preventDefault(); active ? removeBottomFilter(e, fValue) : addBottomFilter(e, fValue) }}
													className={active ? "active" : ""}
												>
													{fValue.name}
												</a>
											}
										</>
									})}
								</nav>
							</div>
						))}
					</>
				)}
			</nav>
			<Loading />
		</nav>
	)
})

export default Navigation
